import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, watch } from 'vue';
import BusinessLogicHelper from '@/core/lib/businessLogicHelper';
import { addLiveCountToTodayCount } from '@/core/lib/sportMarketInfoHelper';
import { getMarketPageFromPresetAndDate } from '@/core/oddsApi/helpers';
import { EnumEventDateFilterType, EnumPresetFilterType, EnumTimeZoneType, useEventsCountInfoQuery } from '@/core/oddsApi/oddsApiType';
import type { IMarketInfo } from '@/interface/IMarketInfo';
import type { IZoneEventCount } from '@/interface/ISportMarketCount';
import type { PromotionZone } from '@/interface/enum';
import { EventDateType, MarketPage, SiteStyle, SportType } from '@/interface/enum';
import { SportInfo } from '@/models/SportInfo';
import { useOddsApiStore } from '@/store/oddsApiStore';
import { useToggleStore } from '@/store/toggleStore';

const filters = [
    { presetFilter: EnumPresetFilterType.Live, date: EnumEventDateFilterType.All },
    { presetFilter: EnumPresetFilterType.NonLive, date: EnumEventDateFilterType.Today },
    { presetFilter: EnumPresetFilterType.NonLive, date: EnumEventDateFilterType.EarlyMarket },
    { presetFilter: EnumPresetFilterType.NonLive, date: EnumEventDateFilterType.Plus1 },
    { presetFilter: EnumPresetFilterType.NonLive, date: EnumEventDateFilterType.Plus2 },
    { presetFilter: EnumPresetFilterType.NonLive, date: EnumEventDateFilterType.Plus3 },
    { presetFilter: EnumPresetFilterType.NonLive, date: EnumEventDateFilterType.Plus4 },
    { presetFilter: EnumPresetFilterType.NonLive, date: EnumEventDateFilterType.Plus5 },
    { presetFilter: EnumPresetFilterType.NonLive, date: EnumEventDateFilterType.Plus6Over },
    { presetFilter: EnumPresetFilterType.Outright, date: EnumEventDateFilterType.All },
];

export function useSingleSportsEventCount({
    sportType,
    promotionZone,
    filteredLeagueIds,
    filteredLeagueNames,
    isEnabled,
}: {
    sportType: Ref<SportType>;
    promotionZone: Ref<PromotionZone>;
    filteredLeagueIds: Ref<number[]>;
    filteredLeagueNames: Ref<string[]>;
    isEnabled: Ref<boolean>;
}): IZoneEventCount {
    const { isUseSoccerTodayExpand } = storeToRefs(useToggleStore(SiteStyle.Asia));
    // Match the soccer Today list: count the TodayExpanded window instead of Today.
    const isSoccerTodayExpand = computed(() => isUseSoccerTodayExpand.value && sportType.value === SportType.Soccer);

    const queryFilters = computed(() => filters.map(filter => (isSoccerTodayExpand.value
        && filter.presetFilter === EnumPresetFilterType.NonLive
        && filter.date === EnumEventDateFilterType.Today
        ? { ...filter, date: EnumEventDateFilterType.TodayExpanded }
        : filter)));

    const variables = computed(() => ({
        query: {
            timeZone: EnumTimeZoneType.UTC__4,
            filters: queryFilters.value,
            sport: SportType.toOddsApi(sportType.value),
            tournamentIds: filteredLeagueIds.value,
            tournamentNames: filteredLeagueNames.value,
        },
    }));

    const { result, loaded, loading } = useEventsCountInfoQuery(
        variables,
        computed(() => ({
            pollInterval: 30000,
            enabled: isEnabled.value,
        })),
    );

    const { customerSettings } = storeToRefs(useOddsApiStore());
    const filteredCount = computed(() => result.value?.map(x => ({
        queryFilter: x.queryFilter,
        liveEventCount: BusinessLogicHelper.filterEventCount({
            eventCount: x.liveEventCount,
            simulationEventCount: x.liveSimulationEventCount,
            filterEFootballEventCount: x.liveFilterEFootballEventCount,
        }, customerSettings.value, SportType.toOddsApi(sportType.value)),
        eventCount: BusinessLogicHelper.filterEventCount({
            eventCount: x.eventCount,
            simulationEventCount: x.simulationEventCount,
            filterEFootballEventCount: x.filterEFootballEventCount,
        }, customerSettings.value, SportType.toOddsApi(sportType.value)),
    })) ?? []);

    const marketList = computed(() => {
        const list = filteredCount.value?.map<IMarketInfo>((x) => {
            // NonLive + TodayExpanded is the soccer Today list filter; map it to the Today tab.
            const res = x.queryFilter.date === EnumEventDateFilterType.TodayExpanded
                ? { marketPage: MarketPage.Today, date: EventDateType.All }
                : getMarketPageFromPresetAndDate(x.queryFilter.presetFilter, x.queryFilter.date);

            if (!res) {
                return {
                    sportType: sportType.value,
                    marketPage: MarketPage.Today,
                    date: EventDateType.All,
                    nonLiveEventCount: 0,
                    liveEventCount: 0,
                    totalEventCount: 0,
                };
            }

            const { marketPage, date } = res;

            return {
                sportType: sportType.value,
                marketPage,
                date,
                nonLiveEventCount: Math.max(0, x.eventCount - x.liveEventCount),
                liveEventCount: Math.max(0, x.liveEventCount),
                totalEventCount: Math.max(0, x.eventCount),
            };
        }) ?? [];

        addLiveCountToTodayCount(list);

        return new Map<SportType, IMarketInfo[]>([[sportType.value, list]]);
    });

    const sportList = computed<SportInfo[]>(() => {
        const list = [];
        const outrightCount = filteredCount.value?.find(x => x.queryFilter.presetFilter === EnumPresetFilterType.Outright);
        const liveEventCount = filteredCount.value?.find(x => x.queryFilter.presetFilter === EnumPresetFilterType.Live)?.liveEventCount ?? 0;
        const nonLiveEventCount = filteredCount.value?.filter(x => x.queryFilter.presetFilter === EnumPresetFilterType.NonLive && [EnumEventDateFilterType.EarlyMarket, EnumEventDateFilterType.Today, EnumEventDateFilterType.TodayExpanded].includes(x.queryFilter.date)).reduce((acc, x) => acc + x.eventCount, 0);
        list.push(new SportInfo({
            sportType: sportType.value,
            liveEventCount,
            nonLiveEventCount,
            outrightLiveEventCount: outrightCount?.liveEventCount ?? 0,
            outrightNonLiveEventCount: (outrightCount?.eventCount ?? 0) - (outrightCount?.liveEventCount ?? 0),
        }));

        return list;
    });

    watch([promotionZone, sportType, filteredLeagueIds, filteredLeagueNames], () => {
        loaded.value = false;
    });

    return {
        sportList,
        marketList,
        loaded,
        loading,
    };
}
